/// <amd-module name="Wit/WorkItemLinksRegistration/resolveArtifacts" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IObservableArray } from "VSS/Core/Observable";
import { IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import { ILinkIdentifier, IResolvedLinkDisplayData } from "./Contracts";
import { ILinkTypeRegistry, ILinkTypeRegistryId } from "./registerLinkTypeService";
import { IRegisteredLinkTypeService } from "./IRegisteredLinkTypeService";
import { isSameLinkIdentifier, makePlaceholderForUnresolvedLinkIdentifier } from "./LinkUtils";
import { ArtifactCache } from "./ArtifactCache";
import { ToolIds } from "./Constants";

/*
If you're wondering about the `Promise<Promise<IResolvedLinkDisplayData[]>[]>` type, here's the deal:

`Promise<IResolvedLinkDisplayData[]>` is straightforward; we're resolving data for more than one
artifact, so we return a promise of an array.

But we don't necessarily want to wait for all artifacts to resolve before we can show any data, so
each service implementation can return its data in as few or as many promises as it wants.
Hence `Promise<IResolvedLinkDisplayData[]>[]`

The last piece is that each service is potentially loaded asynchronously.
Hence `Promise<Promise<IResolvedLinkDisplayData[]>[]>`.

Is it gross? Yes. But it ends up creating a better UX by allowing us to show fast-loading links as
soon as they are available, and we hide away the ugliness behind a few functions with simple
signatures.
*/

/**
 * Loads display data for a single link identifier
 */
export async function resolveArtifact(
    linkIdentifier: ILinkIdentifier,
    workItemId: number,
    pageContext: IVssPageContext
): Promise<IResolvedLinkDisplayData | undefined> {
    const [artifact] = await resolveAllArtifacts([linkIdentifier], workItemId, pageContext);
    return artifact;
}

/**
 * Loads display data for a list of link identifiers. Returns a single promise that will resolve
 * when all links are loaded.
 */
export async function resolveAllArtifacts(
    linkIdentifiers: ILinkIdentifier[],
    workItemId: number,
    pageContext: IVssPageContext
): Promise<IResolvedLinkDisplayData[]> {
    const promisesByTool = resolveArtifactsByTool(linkIdentifiers, workItemId, pageContext);
    const allDisplayDataPromiseArrays = await Promise.all(Object.values(promisesByTool));

    // Flatten the 2d array of promises
    const allPromises: Promise<IResolvedLinkDisplayData[]>[] = [];
    for (const displayDataPromises of allDisplayDataPromiseArrays) {
        for (const displayDataPromise of displayDataPromises) {
            allPromises.push(displayDataPromise);
        }
    }

    const allDisplayDataArrays = await Promise.all(allPromises);

    // Flatten the 2d array of display data
    let resolvedDisplayData: IResolvedLinkDisplayData[] = [];
    for (const displayDataArray of allDisplayDataArrays) {
        resolvedDisplayData = resolvedDisplayData.concat(displayDataArray);
    }

    // Make placeholders for link identifiers that do not match any of the successfully-resolved data
    const placeholdersForUnresolvedIdentifiers = linkIdentifiers
        .filter(linkIdentifier => !resolvedDisplayData.find(dd => isSameLinkIdentifier(linkIdentifier, dd!.linkIdentifier)))
        .map(ld => makePlaceholderForUnresolvedLinkIdentifier(ld))
        .filter(p => !!p) as IResolvedLinkDisplayData[];

    return resolvedDisplayData.concat(placeholdersForUnresolvedIdentifiers);
}

const outputObservablesBeingProcessed = new Set<IObservableArray<IResolvedLinkDisplayData | undefined>>();

/**
 * Loads display data for a list of link identifiers. Modifies an observable array with the resolved link display data as it loads.
 */
export function observeArtifactResolution(
    linkIdentifiers: ILinkIdentifier[],
    workItemId: number,
    pageContext: IVssPageContext,
    outputObservable: IObservableArray<IResolvedLinkDisplayData | undefined>,
    ignoreArtifactCache?: boolean,
    comparer?: (a: IResolvedLinkDisplayData, b: IResolvedLinkDisplayData) => number,
    witLinkTypeEnds?: Map<string | number, IWorkItemLinkTypeEnd>
): void {
    if (outputObservablesBeingProcessed.has(outputObservable)) {
        return;
    }
    outputObservablesBeingProcessed.add(outputObservable);

    const initialContents: undefined[] = new Array(linkIdentifiers.length).fill(undefined);
    outputObservable.value = initialContents;

    const cache = ArtifactCache.getInstance();

    for (const linkIdentifier of linkIdentifiers) {
        if (
            ignoreArtifactCache === true &&
            (linkIdentifier.ID || linkIdentifier.tool === ToolIds.ReleaseManagement || linkIdentifier.tool === ToolIds.Git)
        ) {
            cache.clear(linkIdentifier);
        }
    }

    const topLevelPromisesByTool = resolveArtifactsByTool(linkIdentifiers, workItemId, pageContext);

    const promiseCounts = {
        totalTopLevel: Object.keys(topLevelPromisesByTool).length,
        completedTopLevel: 0,
        totalDisplayData: 0,
        completedDisplayData: 0
    };

    const finish = () => {
        // We have loaded all of the data for all of the promises; clear out the rest of the undefined
        // values from the array if there are any (these would be artifacts that couldn't be resolved).
        outputObservable.removeAll(item => !item);

        const placeholdersForUnresolvedIdentifiers = linkIdentifiers
            .filter(linkIdentifier => !outputObservable.value.find(dd => isSameLinkIdentifier(linkIdentifier, dd!.linkIdentifier)))
            .map(ld => {
                const witLinkTypeEnd = ld.LinkType ? witLinkTypeEnds?.get(ld.LinkType) : undefined;
                return makePlaceholderForUnresolvedLinkIdentifier(ld, witLinkTypeEnd?.name, witLinkTypeEnd?.immutableName);
            })
            .filter(p => !!p);

        if (placeholdersForUnresolvedIdentifiers.length > 0) {
            outputObservable.push(...placeholdersForUnresolvedIdentifiers);
        }

        outputObservablesBeingProcessed.delete(outputObservable);
        if (comparer)
            outputObservable.value =  outputObservable.value.sort(comparer);
    };

    if (Object.values(topLevelPromisesByTool).length === 0) {
        finish();
        return;
    }

    for (const promise of Object.values(topLevelPromisesByTool)) {
        promise
            .then(displayDataPromises => {
                // These displayDataPromises are the promises returned from the link type service implementation
                promiseCounts.completedTopLevel += 1;
                promiseCounts.totalDisplayData += displayDataPromises.length;

                for (const displayDataPromise of displayDataPromises) {
                    displayDataPromise
                        .then(resolvedDisplayData => {
                            promiseCounts.completedDisplayData += 1;
                            const tempOutput = outputObservable.value;
                            let shouldOutputObservableBeUpdated = false;
                            for (const resolvedLink of resolvedDisplayData) {
                                // Replace the first undefined value in the array with the resolved data for this artifact
                                const firstUndefinedIndex = tempOutput.findIndex(item => !item);
                                if (firstUndefinedIndex > -1) {
                                    tempOutput.splice(firstUndefinedIndex, 1, resolvedLink);
                                    shouldOutputObservableBeUpdated = true;
                                }
                            }

                            if (shouldOutputObservableBeUpdated) {
                                outputObservable.value = tempOutput;
                            }

                            if (
                                promiseCounts.totalTopLevel === promiseCounts.completedTopLevel &&
                                promiseCounts.totalDisplayData === promiseCounts.completedDisplayData
                            ) {
                                finish();
                            }
                        })
                        .catch(() => {
                            promiseCounts.completedDisplayData += 1;

                            if (
                                promiseCounts.totalTopLevel === promiseCounts.completedTopLevel &&
                                promiseCounts.totalDisplayData === promiseCounts.completedDisplayData
                            ) {
                                // This was the last display data promise
                                finish();
                            }
                        });
                }
            })
            .catch(() => {
                // A top level promise rejected ;
                promiseCounts.completedTopLevel += 1;

                if (promiseCounts.totalTopLevel === promiseCounts.completedTopLevel) {
                    // This was the last top-level promise
                    finish();
                }
            });
    }
}

function resolveArtifactsByTool(
    linkIdentifiers: ILinkIdentifier[],
    workItemId: number,
    pageContext: IVssPageContext
): { [tool: string]: Promise<Promise<IResolvedLinkDisplayData[]>[]> } {
    const identifiersByTool = new Map<string, ILinkIdentifier[]>();
    for (const linkIdentifier of linkIdentifiers) {
        if (!identifiersByTool.has(linkIdentifier.tool)) {
            identifiersByTool.set(linkIdentifier.tool, [linkIdentifier]);
        } else {
            identifiersByTool.get(linkIdentifier.tool)!.push(linkIdentifier);
        }
    }

    const displayDataPromiseByTool: { [tool: string]: Promise<Promise<IResolvedLinkDisplayData[]>[]> } = {};

    for (const [tool, artifacts] of identifiersByTool.entries()) {
        displayDataPromiseByTool[tool] = getArtifactResolutionPromisesForTool(tool, artifacts, workItemId, pageContext);
    }

    return displayDataPromiseByTool;
}

async function getArtifactResolutionPromisesForTool(
    tool: string,
    linkIdentifiers: ILinkIdentifier[],
    workItemId: number,
    pageContext: IVssPageContext
): Promise<Promise<IResolvedLinkDisplayData[]>[]> {
    // First, try to get as many artifacts as possible from the cache
    const cache = ArtifactCache.getInstance();
    const linkIdentifiersToLoad: ILinkIdentifier[] = [];
    const cachedData: IResolvedLinkDisplayData[] = [];
    for (const linkIdentifier of linkIdentifiers) {
        const cachedItem = cache.get(linkIdentifier);
        if (cachedItem) {
            cachedData.push(cachedItem);
        } else {
            // Not cached - this artifact must be loaded from the service
            linkIdentifiersToLoad.push(linkIdentifier);
        }
    }

    if (linkIdentifiersToLoad.length === 0) {
        // Everything was cached - no need to do more work.
        return [Promise.resolve(cachedData)];
    }

    try {
        const service = await getService(pageContext, tool);
        const promises = service.resolveArtifacts(linkIdentifiersToLoad, workItemId);

        for (const promise of promises) {
            // When these promises resolve, add their resolved data to the cache.
            promise.then(resolvedData => {
                for (const resolvedItem of resolvedData) {
                    cache.set(resolvedItem.linkIdentifier, resolvedItem);
                }
            });
        }

        return [Promise.resolve(cachedData), ...promises];
    } catch (e: unknown) {
        console.error("Unable to resolve artifacts: ", linkIdentifiers, e);
        return [Promise.resolve(cachedData)];
    }
}

async function getService(pageContext: IVssPageContext, tool: string): Promise<IRegisteredLinkTypeService> {
    const registry = pageContext.getService<ILinkTypeRegistry>(ILinkTypeRegistryId);
    const service = await registry.getRegisteredLinkTypeService(tool).catch(() => undefined);
    if (!service) {
        return Promise.reject(new Error(`No link type service avaiable for tool ${tool}`));
    }
    return service;
}
